import { Dictionary, RouteLocation, RouteLocationNormalizedLoaded, useRoute } from 'vue-router';
import {
	MixpanelActionProperties,
	MixpanelClickProperties,
	MixpanelViewProperties,
	MixpanelViewType
} from 'types/analytics';
import { addBreadcrumb } from '@sentry/vue';
import { allowedMxpQueryParams } from '@constants/mixpanel';
import { flattenQueryParam } from '@utils/request';
import { getSessionReplayLink } from '@utils/datadog';
import { hasMixpanel } from '@utils/analytics';
import { kebabCaseToTitleCase } from '@filters/shared-filters';
import mixpanel from 'mixpanel-browser';

export function useMixpanel(to?: RouteLocation) {
	const route: RouteLocationNormalizedLoaded | RouteLocation = to ?? useRoute();

	function getMixpanelViewName() {
		let viewName = '';

		if (route?.meta?.customAnalyticsViewName) {
			viewName = route?.meta?.customAnalyticsViewName;
		} else if (route?.name) {
			viewName = kebabCaseToTitleCase(route?.name as string);
		} else {
			viewName = route?.path ?? '';
		}

		return viewName;
	}

	function getMixpanelViewId() {
		const idFromRoute = Object.entries(route?.params ?? {}).find(([key]) => /id|slug/i.test(key))?.[1] ?? null;
		return flattenQueryParam(idFromRoute) ?? undefined;
	}

	function getUrlAndParams() {
		if (route) {
			const params = processQueryParamsForMixpanel(route?.query);
			const datadogSessionReplayLink = getSessionReplayLink();
			if (datadogSessionReplayLink) {
				params['Session Replay Link'] = datadogSessionReplayLink;
			}

			return {
				'Page URL': route?.path,
				'Sanitized Page URL': sanitizePageUrl(route?.path),
				'View Name': getMixpanelViewName(),
				'View ID': getMixpanelViewId(),
				...params
			};
		}
	}

	function processQueryParamsForMixpanel(query: Dictionary<(string | null) | (string | null)[]>): {
		[key: string]: string;
	} {
		const params: Record<string, string> = {};
		const entries = Object.entries(query);

		for (const [key, val] of entries) {
			if (allowedMxpQueryParams.includes(key)) {
				const localKey = key === 'cta' ? 'Referring CTA' : key;
				if (val) {
					params[localKey] = val.toString();
				}
			}
		}

		return params;
	}

	function sanitizePageUrl(pageUrl: string): string {
		return pageUrl.replace(/\d+/g, 'X');
	}

	function sanitizeNumbers(text: string | undefined): string | undefined {
		if (!text) {
			return text;
		}

		return text.replace(/(\d[0-9,.]*\d|\d)[kMB]?/g, 'X');
	}

	function getImageFileName(text: string): string {
		// Extract the file name from a path, handling both forward and backward slashes
		const parts = text.split(/[/\\]/);
		return parts[parts.length - 1];
	}

	function getFallBackActionTarget(children: HTMLCollection): string | undefined {
		if (children.length === 0) {
			return undefined;
		}

		for (let i = 0; i < children.length; i++) {
			const element = children.item(i);

			if (element) {
				const tagName = element.tagName?.toLowerCase();

				if (tagName === 'svg') {
					const name = element.getAttribute('name');
					return `Icon-${name}`;
				}

				if (tagName === 'img') {
					const alt = element.getAttribute('alt');
					const src = element.getAttribute('src');
					if (alt) {
						return `Img-${alt}`;
					} else if (src) {
						return `Img-${getImageFileName(src)}`;
					}
				}

				if (!!element.textContent) {
					return sanitizeNumbers(element.textContent) ?? '';
				}

				if (!!tagName) {
					return `tag-${tagName}`;
				}
			}
		}

		return undefined;
	}

	function findMixpanelContext(element: HTMLElement | null | undefined): string {
		if (element) {
			const attr = element.getAttribute('data-context');
			if (attr) {
				return attr;
			} else if (element.parentElement) {
				return findMixpanelContext(element.parentElement);
			}
		}
		return '';
	}

	function getActionTargetFromContext(elm: HTMLElement): string | undefined {
		const dataActionName = elm?.getAttribute('data-dd-action-name') ?? undefined;
		const elTextContent = !!elm?.textContent ? elm?.textContent : undefined;
		const fallbackText = elm?.children?.length ? getFallBackActionTarget(elm.children) : undefined;
		const dataTestName = elm?.getAttribute('data-test') ?? undefined;
		return dataActionName ?? elTextContent ?? fallbackText ?? dataTestName;
	}

	function getActionTarget(clickData: MixpanelClickProperties): string {
		const definedTarget = clickData['Action Target'];
		const actionTargetFromContext = clickData.element?.value
			? getActionTargetFromContext(clickData.element?.value)
			: undefined;

		return definedTarget ?? actionTargetFromContext ?? 'unknown';
	}

	function getMixpanelClickProperties(clickData: MixpanelClickProperties): MixpanelClickProperties {
		const clickProperties: MixpanelActionProperties = { 'Action Type': 'Click' };
		const actionContextString = 'Action Context';
		const actionElementString = 'Action Element';

		clickProperties['Action Target'] = getActionTarget(clickData);

		if (clickData[actionContextString]) {
			clickProperties[actionContextString] = clickData[actionContextString];
		} else if (clickData.element) {
			clickProperties[actionContextString] = findMixpanelContext(clickData.element.value);
		}

		if (clickData[actionElementString]) {
			clickProperties[actionElementString] = clickData[actionElementString];
		} else {
			clickProperties[actionElementString] =
				clickData.element?.value?.tagName.toLowerCase() ??
				clickData.element?.value?.parentElement?.tagName.toLowerCase() ??
				'';
		}

		if (clickData.Link) {
			clickProperties.Link = clickData.Link;
		}

		return clickProperties;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	function trackMixpanelEvent(name: string, properties = {} as any): void {
		const eventProperties = { ...getUrlAndParams(), ...properties };

		if (hasMixpanel()) {
			mixpanel.track(name, eventProperties);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	function trackMixpanelEventWithBeacon(name: string, properties = {} as any): void {
		const eventProperties = { ...getUrlAndParams(), ...properties };

		if (hasMixpanel()) {
			mixpanel.track(name, eventProperties, { transport: 'sendBeacon' });
		}
	}

	function trackMixpanelView(viewType: MixpanelViewType, properties?: Partial<MixpanelViewProperties>): void {
		if (hasMixpanel()) {
			const { customProperties, ...standardProperties } = properties || {};
			const viewProperties = {
				...standardProperties,
				...customProperties,
				'View Type': viewType
			};

			if (viewType === 'Modal' || viewType === 'Element') {
				trackMixpanelEventWithBeacon('View', viewProperties);
			} else {
				trackMixpanelEvent('View', viewProperties);
			}
		}
	}

	function trackMixpanelClick(clickData: MixpanelClickProperties, useBeacon = false): void {
		if (hasMixpanel()) {
			const properties = { ...getMixpanelClickProperties(clickData), ...clickData.customProperties };
			if (useBeacon) {
				trackMixpanelEventWithBeacon('Action', properties);
			} else {
				trackMixpanelEvent('Action', properties);
			}

			addBreadcrumb({
				category: 'mixpanel',
				message: 'Action',
				level: 'info',
				data: properties
			});
		}
	}

	function trackMixpanelScrollIntoView(
		target: HTMLElement,
		mixpanelProperties: Partial<MixpanelViewProperties>,
		once = true
	): void {
		let emitted = false;
		const threshold = target.offsetHeight > window.innerHeight ? 0.1 : 0.9;

		const observer = new IntersectionObserver(
			(entries: Array<IntersectionObserverEntry>) => {
				if (!(emitted && once) && entries.some((e) => e.isIntersecting)) {
					trackMixpanelView('Element', mixpanelProperties);
					emitted = true;
				}
			},
			{ threshold }
		);

		observer.observe(target);
		/**
		 * useIntersectionObserver creates build issues while we are on TypeScript 4
		 * We can return to using this composable once we bump to 5.2.2 (@JF)
		const threshold = target.offsetHeight > window.innerHeight ? 0.1 : 0.9;
	
	
	
		useIntersectionObserver(
			target,
			([{ isIntersecting }], observerElement) => {
				if (isIntersecting) {
					trackMixpanelView('Element', mixpanelProperties);
					observerElement.unobserve(target);
				}
			},
			{
				threshold
			}
		);
		 */
	}

	return {
		findMixpanelContext,
		getActionTargetFromContext,
		getMixpanelClickProperties,
		getMixpanelViewName,
		getMixpanelViewId,
		getUrlAndParams,
		processQueryParamsForMixpanel,
		sanitizePageUrl,
		sanitizeNumbers,
		trackMixpanelScrollIntoView,
		trackMixpanelEvent,
		trackMixpanelEventWithBeacon,
		trackMixpanelView,
		trackMixpanelClick
	};
}
